import { Box, BoxProps, Button, Flex, Heading, Text } from '@mezzoforte/forge';
import { useContext } from 'react';
import { ArrowsLeftRight } from '@mezzoforte/forge-icons';
import { BiddingContext } from '@/context/BiddingContext';
import { useHermesEntry } from '@/hooks/useHermesEntry';
import { CATEGORY_APARTMENT } from '@/types/Category';
import { formatCurrency } from '@/util/currency';
import { priceWithVat, priceWithoutVat } from '@/util/price';
import { HermesEntry } from '@/types/ListEntry';
import { useVatlessSelection } from '@/hooks/useVatlessSelection';

function getBidLabel(bidCount: number, isEnded: boolean) {
  if (bidCount > 0) return 'Korkein tarjous';
  if (isEnded) return 'Ei tarjouksia';
  return 'Ei vielä tarjouksia';
}

function getCostMessage(categoryId: number, vatPerc: number, debt?: number | null) {
  if (categoryId === CATEGORY_APARTMENT) {
    if (debt) return 'Ostaja ottaa vastatakseen huoneiston velkaosuuden';
    return '';
  }

  return vatPerc === 0 ? 'Ei vähennettävää arvonlisäveroa' : null;
}

function vatLessToggle(vatPerc: number, showVatlessPrice: boolean, toggleVatless: (vatless: boolean) => void) {
  return (
    <Button
      padding={1}
      paddingLeft={2}
      paddingRight={2}
      minH={1}
      onClick={() => toggleVatless(showVatlessPrice)}
      aria-label={showVatlessPrice ? 'Näytä hinta arvonlisäverottomana' : 'Näytä hinta arvonlisäverollisena'}
    >
      <Flex alignItems="center">
        <Text paddingRight={2}>Alv {showVatlessPrice ? 0 : vatPerc} %</Text>
        <ArrowsLeftRight size={14} />
      </Flex>
    </Button>
  );
}

function LoadingPlaceholder({ children }: React.PropsWithChildren) {
  return (
    <Box
      as="span"
      display="inline"
      background="lineBorder"
      color="lineBorder"
      borderRadius="sm"
    >
      {children}
    </Box>
  );
}

type HighestBidEntry =
  | (HermesEntry & { bidCount: number; highestBid: number })
  | { isEnded: boolean; bidCount: number; highestBid: number };

interface HighestBidProps extends BoxProps {
  entry: HighestBidEntry;
  isBusy: boolean;
  hermesHasEntryBidCount: boolean;
}

function HighestBid({ entry, isBusy, hermesHasEntryBidCount, ...boxProps }: HighestBidProps) {
  const { vatPerc, startPrice, categoryId, debt } = useContext(BiddingContext);
  const { showVatlessPrice, toggleVatless } = useVatlessSelection();
  return (
    <Flex
      {...boxProps}
      mb={3}
      direction="column"
      gap={1}
      aria-busy={isBusy}
    >
      <Text variant="bodySmallBold">
        {!entry && <LoadingPlaceholder>Korkein tarjous</LoadingPlaceholder>}
        {entry && getBidLabel(entry.bidCount, entry.isEnded)}
      </Text>
      {!entry && (
        <Text
          fontSize="2xl"
          fontWeight="semibold"
        >
          <LoadingPlaceholder>Ladataan ...</LoadingPlaceholder>
        </Text>
      )}
      {hermesHasEntryBidCount && (
        <Heading variant="h3">
          {formatCurrency(showVatlessPrice ? entry.highestBid : priceWithVat(entry.highestBid, vatPerc))}
        </Heading>
      )}
      {entry?.bidCount === 0 && (
        <Text fontSize="sm">
          Lähtöhinta{' '}
          <Text
            fontWeight="bold"
            display="inline"
            as="span"
          >
            {formatCurrency(showVatlessPrice ? startPrice : priceWithVat(startPrice, vatPerc))}
          </Text>
        </Text>
      )}
      <Text fontSize="sm">{getCostMessage(categoryId, vatPerc, debt)}</Text>
      {vatPerc > 0 && <Text fontSize="sm">{vatLessToggle(vatPerc, showVatlessPrice, toggleVatless)}</Text>}
    </Flex>
  );
}

export default function EntryHighestBid() {
  const { entryId, vatPerc, bids } = useContext(BiddingContext);
  const { hermesEntry } = useHermesEntry(entryId, vatPerc);

  const allBids = hermesEntry?.bids ?? bids ?? [];

  // Merge API response bid info with real-time data from Hermes
  const rawEntry = hermesEntry ?? { isEnded: true };

  const entry = {
    ...rawEntry,
    bidCount: allBids.length,
    highestBid: priceWithoutVat(allBids[0]?.amount ?? { amount: 0, vatPercent: 0 }).amount,
  };
  return (
    <HighestBid
      data-test="highest-bid"
      entry={entry}
      isBusy={hermesEntry === undefined}
      hermesHasEntryBidCount={!!hermesEntry && hermesEntry.bidCount > 0}
    />
  );
}

export function PreviewHighestBid() {
  const entry = { isEnded: false, bidCount: 0, highestBid: 0 };
  return (
    <HighestBid
      data-test="preview-highest-bid"
      isBusy={false}
      entry={entry}
      hermesHasEntryBidCount={false}
    />
  );
}
